

import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';

const DashboardLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/auth');
  };

  const sidebarItems = [
    { id: 'overview', label: 'Overview', icon: '📊', path: '/dashboard' },
    { id: 'users', label: 'Users', icon: '👥', path: '/dashboard/users' },
    { id: 'feedback', label: 'Feedback', icon: '💬', path: '/dashboard/feedback' },
    { id: 'email', label: 'Email Management', icon: '📧', path: '/dashboard/email' },
    { id: 'careers', label: 'Careers', icon: '💼', path: '/dashboard/careers' },
    { id: 'events', label: 'Events', icon: '🎉', path: '/dashboard/events' },
    { id: 'campus-connect', label: 'Campus Connect', icon: '🏫', path: '/dashboard/campus-connect' },
    { id: 'campus-alpha', label: 'Campus Alpha', icon: '🏫', path: '/dashboard/campus-alpha' },
    { id: 'hackathons', label: 'Hackathons', icon: '🏆', path: '/dashboard/hackathons' },
    { id: 'hackathon-results', label: 'Hackathon Results', icon: '🏅', path: '/dashboard/hackathon-results' },
    { id: 'course-content', label: 'Course Content', icon: '📚', path: '/dashboard/course-content' },
    { id: 'timeline', label: 'Roadmap Timelines', icon: '⏱️', path: '/dashboard/timeline' },
    { id: 'practice', label: 'Practice Problems', icon: '💻', path: '/dashboard/practice' },
    { id: 'takumi-certifications', label: 'Takumi Certifications', icon: '🎓', path: '/dashboard/takumi-certifications' },
    { id: 'takumi-doubts', label: 'Takumi Doubt Classes', icon: '🙋‍♂️', path: '/dashboard/takumi-doubts' },
    { id: 'affiliates', label: 'Affiliate Marketing', icon: '🤝', path: '/dashboard/affiliates' },
  ];


  const handleNavigation = (path) => {
    navigate(path);
    setSidebarOpen(false); // Close sidebar on mobile after navigation
  };

  return (
    <div className="min-h-screen bg-gray-100 flex">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`
        fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out
        lg:translate-x-0 lg:static lg:inset-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex items-center justify-between p-6 lg:justify-start">
          <h1 className="text-xl font-bold text-gray-900">Admin Panel</h1>
          {/* Close button for mobile */}
          <button
            onClick={() => setSidebarOpen(false)}
            className="p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 lg:hidden"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <nav className="mt-6">
          <div className="px-3">
            {sidebarItems.map((item) => (
              <button
                key={item.id}
                onClick={() => handleNavigation(item.path)}
                className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md mb-1 transition-colors ${location.pathname === item.path
                  ? 'bg-blue-100 text-blue-700 border-r-2 border-blue-700'
                  : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                  }`}
              >
                {item.icon && <span className="mr-3 text-base">{item.icon}</span>}
                {item.label}
              </button>

            ))}
          </div>
        </nav>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col lg:ml-0">
        {/* Header */}
        <header className="bg-white shadow-sm border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-4">
              <div className="flex items-center">
                {/* Mobile menu button */}
                <button
                  onClick={() => setSidebarOpen(true)}
                  className="p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 lg:hidden mr-2"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Dashboard</h1>
              </div>

              {/* User info and logout */}
              <div className="flex items-center space-x-4">
                {/* User greeting - hidden on very small screens */}
                <span className="hidden sm:inline text-sm text-gray-500">
                  Welcome, {user?.name || 'User'}
                </span>
                <button
                  onClick={handleLogout}
                  className="bg-red-600 hover:bg-red-700 text-white px-3 py-2 sm:px-4 sm:py-2 rounded-md text-xs sm:text-sm font-medium transition-colors"
                >
                  Logout
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Dynamic Content Area */}
        <main className="flex-1 p-4 sm:p-6 lg:p-8">
          <div className="max-w-7xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;